import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCopyToClipboard } from '@/shared/hooks';
import { Button, Checkbox } from '@/shared/ui';

import { downloadRecoveryCodes, printRecoveryCodes } from '../../lib';

import styles from './RecoveryCodesPanel.module.scss';

type RecoveryCodesPanelProps = {
  codes: string[];
  onContinue: () => void;
  className?: string;
  signInRequired?: boolean;
};

export const RecoveryCodesPanel: FC<RecoveryCodesPanelProps> = (props) => {
  const { codes, onContinue, className, signInRequired = false } = props;

  const { formatMessage } = useIntl();
  const copyToClipboard = useCopyToClipboard();

  const [isSaved, setIsSaved] = useState(false);

  const title = formatMessage({
    id: 'mfa.recoveryCodes.title',
    defaultMessage: 'Save your recovery codes',
  });

  return (
    <div
      className={cn(styles.container, className)}
      data-section="mfa-recovery-codes"
    >
      <div className={styles.intro}>
        <span className={cn(styles.eyebrow, 'p4')}>
          <FormattedMessage
            id="mfa.recoveryCodes.eyebrow"
            defaultMessage="Two-step verification is on"
          />
        </span>

        <h3 className="h3">{title}</h3>

        <p className={cn(styles.description, 'p2')}>
          <FormattedMessage
            id="mfa.recoveryCodes.description"
            defaultMessage="These ten codes are shown once and never again. Keep them somewhere other than the phone holding your authenticator app. Each code can be used once, to turn two-step verification off if you lose access to the app."
          />
        </p>
      </div>

      <div className={styles.codes}>
        {codes.map((code) => (
          <span key={code} className={cn(styles.code, 'p2')}>
            {code}
          </span>
        ))}
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="medium"
          onClick={() => copyToClipboard(codes.join('\n'))}
        >
          <FormattedMessage
            id="mfa.recoveryCodes.copyAll"
            defaultMessage="Copy all"
          />
        </Button>

        <Button
          variant="secondary"
          size="medium"
          onClick={() => downloadRecoveryCodes(codes)}
        >
          <FormattedMessage
            id="mfa.recoveryCodes.download"
            defaultMessage="Download .txt"
          />
        </Button>

        <Button
          variant="secondary"
          size="medium"
          onClick={() => printRecoveryCodes(title, codes)}
        >
          <FormattedMessage id="global.print" defaultMessage="Print" />
        </Button>
      </div>

      <div className={styles.divider} />

      <Checkbox checked={isSaved} onChange={() => setIsSaved(!isSaved)}>
        <FormattedMessage
          id="mfa.recoveryCodes.saved"
          defaultMessage="I have saved these codes somewhere safe"
        />
      </Checkbox>

      <div className={styles.footer}>
        {signInRequired && (
          <p className="p2">
            <FormattedMessage
              id="mfa.recoveryCodes.signInRequired"
              defaultMessage="Your authenticator is set up. Save these codes, then sign in again to finish."
            />
          </p>
        )}
        <Button disabled={!isSaved} onClick={onContinue}>
          {signInRequired ? (
            <FormattedMessage
              id="mfa.forced.signInAgain"
              defaultMessage="Sign in again"
            />
          ) : (
            <FormattedMessage id="global.continue" defaultMessage="Continue" />
          )}
        </Button>
      </div>
    </div>
  );
};
